// API service for backend communication
function normalizeBaseUrl(baseUrl) {
  const raw = String(baseUrl || "")
    .trim()
    .replace(/\/$/, "");
  if (!raw) return "";
  // Asegura que incluya /api/v1 (la app usa endpoints relativos a /api/v1)
  if (raw.endsWith("/api/v1")) return raw;
  return `${raw}/api/v1`;
}

// En este repo, para `saludsa-app-front` el backend a emular es `saludsa-app-backend`
// (función HTTP `consultasV3`) y monta el express app bajo `/api/v1`.
const DEFAULT_LOCAL_BASE =
  "http://127.0.0.1:5001/saludsapro/us-central1/consultasV3";
const DEFAULT_PROD_BASE = "https://consultasv3-46q36qrvoa-uc.a.run.app/api/v1";

const API_BASE_URL = (() => {
  // Prioridad: env var explícita
  if (process.env.VUE_APP_API_BASE_URL) {
    return normalizeBaseUrl(process.env.VUE_APP_API_BASE_URL);
  }
  // En local/dev: usar emulador por defecto
  if (process.env.NODE_ENV === "development") {
    return normalizeBaseUrl(DEFAULT_LOCAL_BASE);
  }
  // Producción: Cloud Run
  return normalizeBaseUrl(DEFAULT_PROD_BASE);
})();

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;

    const defaultOptions = {
      headers: {
        "Content-Type": "application/json",
        ...options.headers,
      },
    };

    const config = {
      ...defaultOptions,
      ...options,
      headers: {
        ...defaultOptions.headers,
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error ||
            errorData.message ||
            `HTTP error! status: ${response.status}`,
        );
      }

      return await response.json();
    } catch (error) {
      console.error("API request failed:", error);
      throw error;
    }
  }

  // Get user profile using token and DNI
  async getUserProfile(token, userId) {
    console.log("API Service - getUserProfile called with:", { token, userId });
    console.log(
      "API Service - Full URL:",
      `${this.baseURL}/app/userProfile/${userId}`,
    );
    console.log("API Service - Headers:", { Authorization: token });

    return this.request(`/app/userProfile/${userId}`, {
      headers: {
        Authorization: token,
      },
    });
  }

  // Update user profile (phone and email)
  async updateUserProfile(token, userId, profileData) {
    console.log("API Service - updateUserProfile called with:", {
      token,
      userId,
      profileData,
    });
    console.log(
      "API Service - Full URL:",
      `${this.baseURL}/profile/user/${userId}`,
    );

    return this.request(`/profile/user/${userId}`, {
      method: "PUT",
      headers: {
        Authorization: token,
      },
      body: JSON.stringify(profileData),
    });
  }

  // Validate if user exists
  async validateUserExists(userId) {
    return this.request("/app/validateUserExists", {
      method: "POST",
      body: JSON.stringify({ userId }),
    });
  }

  // Get banks list
  async getBanks() {
    return this.request(`/banks`);
  }

  // Get user's bank account
  async getUserBankAccount(token, userId) {
    return this.request(`/banks/user/${userId}`, {
      headers: {
        Authorization: token,
      },
    });
  }

  // Create or update user's bank account
  async upsertBankAccount(token, bankData) {
    return this.request(`/banks/upsertAccount`, {
      method: "POST",
      headers: {
        Authorization: token,
      },
      body: JSON.stringify(bankData),
    });
  }

  // Delete user's bank account
  async deleteBankAccount(token, userId) {
    return this.request(`/banks/user/${userId}`, {
      method: "DELETE",
      headers: {
        Authorization: token,
      },
    });
  }

  // Get user's current balance
  async getUserBalance(token, userId) {
    return this.request(`/balance/user/${userId}`, {
      headers: {
        Authorization: token,
      },
    });
  }

  // Get historical referrals (leads)
  async getHistoricalReferrals(token, userId) {
    return this.request(`/balance/user/${userId}/historical-referrals`, {
      headers: {
        Authorization: token,
      },
    });
  }

  // Get current month referrals (leads)
  async getCurrentMonthReferrals(token, userId) {
    return this.request(`/balance/user/${userId}/current-month-referrals`, {
      headers: {
        Authorization: token,
      },
    });
  }

  // Get historical earnings
  async getHistoricalEarnings(token, userId) {
    return this.request(`/balance/user/${userId}/historical-earnings`, {
      headers: {
        Authorization: token,
      },
    });
  }

  // Refresh all balance data (main endpoint for dashboard)
  async refreshAllBalanceData(token, userId) {
    return this.request(`/balance/user/${userId}/refresh`, {
      headers: {
        Authorization: token,
      },
    });
  }

  // Check if user has pending payment request
  async checkPendingPaymentRequest(token, userId) {
    return this.request(`/payments/pending/${userId}`, {
      headers: {
        Authorization: token,
      },
    });
  }

  // Create payment request (withdrawal)
  async createPaymentRequest(token, data) {
    return this.request(`/payments/request`, {
      method: "POST",
      headers: {
        Authorization: token,
      },
      body: JSON.stringify(data),
    });
  }

  // Get user referrals with pagination and filtering
  async getUserReferrals(token, userId, options = {}) {
    const { filter = "all", limit = 10, offset = 0 } = options;
    let endpoint = `/leads/user/${userId}?limit=${limit}&offset=${offset}&filter=${filter}`;

    return this.request(endpoint, {
      headers: {
        Authorization: token,
      },
    });
  }

  // Get user referral statistics
  async getUserReferralStats(token, userId) {
    return this.request(`/leads/stats/${userId}`, {
      headers: {
        Authorization: token,
      },
    });
  }

  // Get user transactions with pagination and filtering
  async getUserTransactions(token, userId, options = {}) {
    const { type = "all", limit = 10, offset = 0 } = options;
    let endpoint = `/transactions/user/${userId}?limit=${limit}&offset=${offset}`;

    if (type !== "all") {
      endpoint += `&type=${type}`;
    }

    return this.request(endpoint, {
      headers: {
        Authorization: token,
      },
    });
  }

  // Get user transaction statistics
  async getUserTransactionStats(token, userId) {
    return this.request(`/transactions/stats/${userId}`, {
      headers: {
        Authorization: token,
      },
    });
  }

  // Add contact/lead to HubSpot
  async addContact(contactData) {
    const url =
      "https://consultasv2-46q36qrvoa-uc.a.run.app/api/v1/hubspot/addContact";

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(contactData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const customError = new Error(
        errorData.error || `HTTP error! status: ${response.status}`,
      );
      customError.status = response.status;
      customError.error = errorData.error;
      customError.data = errorData;
      throw customError;
    }

    return await response.json();
  }

  // -----------------------------
  // Ruleta (spins / prizes)
  // -----------------------------

  async getSpins(token) {
    return this.request(`/balance/spins`, {
      headers: {
        Authorization: token,
      },
    });
  }

  async useSpin(token) {
    return this.request(`/balance/spins/use`, {
      method: "POST",
      headers: {
        Authorization: token,
      },
    });
  }

  async getWheelItems() {
    // Público en backend (sin auth), pero soporta auth si se manda.
    return this.request(`/prize/wheel/items`);
  }

  async winPrize(token, payload) {
    return this.request(`/prize/win`, {
      method: "POST",
      headers: {
        Authorization: token,
      },
      body: JSON.stringify(payload),
    });
  }

  async getUserPrizes(token) {
    return this.request(`/prize/user/prizes`, {
      headers: {
        Authorization: token,
      },
    });
  }

  async redeemPrize(token, prizeId, redeemData) {
    return this.request(`/prize/${prizeId}/redeem`, {
      method: "PUT",
      headers: {
        Authorization: token,
      },
      body: JSON.stringify({ redeemData }),
    });
  }

  // Feature flags
  async getFlag(token, flagId) {
    return this.request(`/flags/${flagId}`, {
      headers: {
        Authorization: token,
      },
    });
  }
}

export default new ApiService();
